// Recovery: out-of-combat healing in valid zones (A-11, §17.6) + building heal-over-time.

import { buildingDef, unitDef } from '../../content/registry';
import { TUNING } from '../../content/tuning';
import { dist } from '../../shared/vec';
import type { Sim } from '../sim';
import type { Squad } from '../state';
import { buildingPassives } from './annexes';

const dt = TUNING.dt;

/** Recovery auras: Field Overhaul (Large-only) and squad-wide auras (defense groves). */
function recoveryAuraMult(sim: Sim, s: Squad): number {
  const sd = unitDef(s.defId);
  const isLarge = sd.sizeTag === 'large' || sd.sizeTag === 'giant';
  let best = 1;
  for (const b of sim.state.buildings.values()) {
    if (b.owner !== s.owner || b.state !== 'active') continue;
    for (const passive of buildingPassives(buildingDef(b.defId))) {
      if (passive.kind === 'largeRecoveryAura') {
        if (!isLarge) continue;
        if (sim.state.time - b.lastDamagedAt < 5) continue; // pauses while the depot is under attack
        if (dist(b.pos, s.pos) <= passive.radius) best = Math.max(best, passive.mult);
      } else if (passive.kind === 'squadRecoveryAura') {
        if (dist(b.pos, s.pos) <= passive.radius) best = Math.max(best, passive.mult);
      }
    }
  }
  return best;
}

/** Shared definition of the friendly territory where an out-of-combat squad can recover. */
export function isInSquadRecoveryZone(sim: Sim, s: Squad): boolean {
  const core = sim.coreAlive(s.owner);
  if (core && dist(s.pos, core.pos) <= sim.map.coreTerritoryRadius) return true;
  for (const site of sim.state.sites) {
    if (site.owner === s.owner && dist(s.pos, site.pos) <= TUNING.defendRadius) return true;
  }
  return false;
}

export function stepRecovery(sim: Sim): void {
  const st = sim.state;
  for (const s of st.squads.values()) {
    if (s.health >= s.maxHealth) continue;
    if (st.time - s.lastCombatAt < TUNING.recoveryOutOfCombatSeconds) continue;
    if (st.time - s.lastDamagedAt < TUNING.recoveryOutOfCombatSeconds) continue;
    if (!isInSquadRecoveryZone(sim, s)) continue;
    let rate = s.isBoss ? TUNING.bossRecoveryPerSecond : TUNING.recoveryPerSecond;
    rate *= sim.annexMult(s.owner, 'recoveryMult');
    rate *= s.healingTakenMult;
    rate *= recoveryAuraMult(sim, s);
    s.health = Math.min(s.maxHealth, s.health + s.maxHealth * rate * dt);
  }

  // player-level timed effects
  for (const p of st.players) {
    if (p.awakeningRemaining > 0) p.awakeningRemaining -= dt;
    if (p.convergenceRemaining > 0) p.convergenceRemaining -= dt;
  }

  // building heal-over-time (Emergency Masonry) + regeneration + temporary expiry
  for (const b of [...st.buildings.values()]) {
    if (b.repairHalvedRemaining > 0) b.repairHalvedRemaining -= dt;
    if (b.fireSuppressedRemaining > 0) b.fireSuppressedRemaining -= dt;
    if (b.tempHealth > 0) {
      b.tempHealthRemaining -= dt;
      if (b.tempHealthRemaining <= 0) b.tempHealth = 0;
    }
    for (let i = b.healOverTime.length - 1; i >= 0; i--) {
      const h = b.healOverTime[i];
      let heal = h.perSecond * dt;
      // healing −50% while under siege damage (bible IC-17)
      if (st.time - b.lastSiegeDamagedAt < 2) heal *= 0.5;
      b.health = Math.min(b.maxHealth, b.health + heal);
      h.remaining -= dt;
      if (h.remaining <= 0) b.healOverTime.splice(i, 1);
    }
    // living structures regenerate out of combat (Verdant); fire suppresses it, and
    // The Great Awakening lets it run through light damage
    const bd = buildingDef(b.defId);
    if (bd.regenOutOfCombat && b.state === 'active' && b.health < b.maxHealth && b.fireSuppressedRemaining <= 0) {
      const awakened = st.players[b.owner].awakeningRemaining > 0;
      const quiet = st.time - b.lastDamagedAt > 12;
      if (quiet || (awakened && st.time - b.lastSiegeDamagedAt > 4)) {
        b.health = Math.min(b.maxHealth, b.health + b.maxHealth * bd.regenOutOfCombat * dt);
      }
    }
    // temporary structures expire quietly
    if (b.expiresRemaining !== null) {
      b.expiresRemaining -= dt;
      if (b.expiresRemaining <= 0) sim.destroyBuildingSilently(b);
    }
  }
}
